import { Dispatch, Middleware } from '@reduxjs/toolkit';
import { authManager, noTokenRedirect } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';

import { appActions } from 'app/app.actions';
import { AppDispatch, AppState } from 'bootstrap.store';
import { getRequestID } from 'components/shared/util/request';
import { debugLog } from 'utils/debugLog';
import { pubSub } from 'utils/pubSub';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import { actions as conversationActions } from '../../../components/conversation/actions/conversation';
import {
  errorReconnectAttempt,
  errorReconnectAttemptReset,
} from '../../../components/errors/reducers/errors';
import { notificationActions } from '../../../components/notifications/actions/notification';
import { presenceActions } from '../../../components/presence/actions/presenceActions';

import {
  websocketActions,
  WebsocketCreateAction,
  WebsocketRequestAction,
} from './actions/websocket';
import { replayReconnectSubscriptions } from './reconnectReplay';
import { responseDeffers } from './responseDeffers';
import {
  getSocketOrWorker,
  SharedWorkerImplementation,
} from './socketInterface';
import { websocketSubscriptions } from './subscriptions';
import {
  WebsocketRequestMessage,
  WebsocketSubscriptionMessage,
} from './types/outboundWebsocketEvent';

const { twilioClose } = twilioActions;

const { notificationFaviconChange } = notificationActions;
const { presenceShowOffline } = presenceActions;

const { conversationGet } = conversationActions;

export const websocketMiddleware =
  (): Middleware<void, AppState, Dispatch> => (store) => {
    const dispatch: AppDispatch = store.dispatch;
    /**
     * We use the `action.payload?.init` flag in `apps/agent-interface/src/assets/app/middleware/websocket/sagas/websocket.ts` to refetch presence.
     * We want to skip this only on the first connection, so we use this variable to track if we have already completed the handshake.
     * When a tab goes dormant it can lose the connection with the worker, and when it reconnects, it goes through the same phases as an initial load goes,
     * but because the app is still alive the components don't rerender and no data is fetched, so we have to treat it as a reconnect we want to refetch presence again.
     * We only skip the first time we connect to the worker because that time the data is fetched by the components.
     * */
    let hasCompletedHandshake = false;
    return (next: AppDispatch) => (action) => {
      try {
        let die = false;

        const init = async (createAction: WebsocketCreateAction) => {
          const socketOrWorker = await getSocketOrWorker();

          try {
            await socketOrWorker.start();

            socketOrWorker.onMessage((data) => {
              if ('type' in data) {
                switch (data.type) {
                  case 'auth_failure': {
                    // App token is invalid.
                    socketOrWorker.close();
                    die = true;
                    next(notificationFaviconChange({ type: 'offline' })); // Update favicon.

                    Sentry.captureMessage('websocket auth failure', {
                      level: 'debug',
                      tags: {
                        team: SentryTeam.Platform,
                      },
                    });
                    console.error('Websocket auth failure');
                    authManager.removeAuthData();

                    ampli.websocketConnectionClosed({
                      source: 'auth_failure_message',
                    });

                    next(twilioClose(data));
                    next(presenceShowOffline());
                    // Redirect the browser to login / signin page.
                    noTokenRedirect();
                    break;
                  }
                  case 'connection:active_connection_changed': {
                    // Connection active changed modal show.
                    // We got a connection changed. Telling us that the api
                    // has detected a new connection and closes this websocket connection.
                    // die must be set before disconnectPort(): it invokes onClose synchronously.
                    die = true;
                    socketOrWorker.disconnectPort();

                    ampli.websocketConnectionClosed({
                      source: 'active_connection_changed_message',
                    });

                    const currentState = store.getState();

                    dispatch(
                      appActions.showModal({
                        modal: { type: 'app:webSocketConnectionChanged' },
                      }),
                    );

                    const oldConnectionId =
                      currentState.websocket.connectionId ?? '';
                    const newConnectionId = data.params.new_connection_id;

                    debugLog('WEBSOCKET-MANAGER', 'Active connection changed', {
                      oldConnectionId,
                      newConnectionId,
                    });
                    ampli.websocketActiveConnectionChanged({
                      oldConnectionId,
                      newConnectionId,
                    });

                    next(presenceShowOffline());
                    next(notificationFaviconChange({ type: 'offline' })); // Update favicon.
                    break;
                  }
                  case 'hello_agent': {
                    const dataCopy = {
                      ...data,
                      init:
                        !hasCompletedHandshake &&
                        createAction.payload?.init === true,
                    };
                    hasCompletedHandshake = true;

                    createAction.meta.resolve(dataCopy); // Hello received, we resolve the promise.

                    next(websocketActions.createFulfilled(dataCopy));
                    next(errorReconnectAttemptReset());
                    next(websocketActions.cancelReconnect());
                    next(
                      appActions.hideModal({
                        type: 'app:networkConnectionLost',
                      }),
                    );

                    const currentState = store.getState();
                    let currentConversationId: undefined | number | null = null;
                    if (
                      currentState.conversation.metaConversation &&
                      currentState.conversation.metaConversation.conversation
                    ) {
                      currentConversationId =
                        currentState.conversation.metaConversation.conversation
                          .id;
                    }

                    replayReconnectSubscriptions({
                      subscriptions: websocketSubscriptions.getAll(),
                      currentConversationId,
                      onReplayCurrentConversation: (csid) => {
                        // Reload only the conversation the user is currently viewing. This will
                        // also resubscribe to events for this conversation through the saga, so
                        // it's not necessary to resubscribe here to prevent duplicate requests.
                        next(conversationGet({ csid }));
                      },
                      onReplayDashboard: () => {
                        // Reload the dashboard queues and also resubscribe to events.
                        pubSub.publish('real_time_dashboard:refetch');
                      },
                      sendSubscription: (subscription) => {
                        // Send tracked subscriptions directly to avoid reconnect replay entering
                        // normal dedupe/ref-count path and being marked as subscription_skipped.
                        socketOrWorker.send(subscription);
                      },
                    });

                    debugLog('WEBSOCKET-MANAGER', 'Connection established');
                    break;
                  }
                  default:
                    // stop worker messages from being sent to the websocket
                    if (data.type.startsWith('worker:')) return;
                    next(websocketActions.eventReceived(data));
                    break;
                }
              } else if ('ping' in data && data.ping != null) {
                // Response to the ping request.
                socketOrWorker.send({
                  pong: data.ping,
                });
              } else if ('error' in data && data.error != null) {
                // data.reply_to to null if response was to a request.
                // So we do not handle it. We just dispatch a WS error.
                next(websocketActions.errorReceived(data.error));
              } else if ('reply_to' in data && data.reply_to != null) {
                // Ws response received.
                const { reply_to, error } = data;
                responseDeffers.handleResponse(reply_to as string, data, error);
                next(websocketActions.replyReceived(data));
              } else {
                console.info('WS: unknown ws event received', data);
              }
            });

            socketOrWorker.onError((error) => {
              debugLog('WEBSOCKET-MANAGER', 'Failed to connect');
              createAction.meta.reject(error);
              next(websocketActions.createRejected(error));
            });

            if (socketOrWorker instanceof SharedWorkerImplementation) {
              socketOrWorker.onPrimaryConnectionChange((isPrimary) => {
                next(websocketActions.connectionPrimary(isPrimary));
              });
            }

            socketOrWorker.onClose(() => {
              // Dispatch the websocketActions.createRejected action
              // to avoid any state where the create WS still is pending,
              // but a close event is intervening before the request is fulfilled.
              if (
                store.getState().websocket.meta.asyncCreateStatus === 'pending'
              ) {
                next(websocketActions.createRejected());
              }

              if (!die) {
                debugLog('WEBSOCKET-MANAGER', 'Disconnected');
                next(websocketActions.closeReceived());
              }
            });
          } catch (exception) {
            console.warn('WS: init exception', exception);
            createAction.meta.reject(exception);
            next(websocketActions.createRejected());
          }
        };

        const handleRequestAction = async (
          payload: WebsocketRequestMessage,
          meta: WebsocketRequestAction['meta'],
        ) => {
          const socketOrWorker = await getSocketOrWorker();

          // Send message via websockets.
          if (socketOrWorker.isConnected) {
            responseDeffers.handleRequest(payload.id as string, meta);

            socketOrWorker.send(payload);
          } else {
            // Maybe here we should add our queue ?
            meta.reject('WS: ws connection is closed');
          }
        };

        const disconnect = async () => {
          const socketOrWorker = await getSocketOrWorker();
          socketOrWorker.close();
        };

        switch (action.type) {
          case websocketActions.request.type: {
            const { payload, meta } = action as WebsocketRequestAction;

            if (payload.id === undefined) {
              payload.id = getRequestID();
            }

            if (
              payload.type === 'connection:subscribe' ||
              payload.type === 'connection:unsubscribe'
            ) {
              const subscriptionAction = websocketSubscriptions.handleRequest(
                payload as WebsocketSubscriptionMessage,
              );

              if (subscriptionAction === 'send') {
                handleRequestAction(payload, meta);
              } else {
                meta.resolve({ type: 'subscription_skipped' });
              }
            } else {
              handleRequestAction(payload, meta);
            }

            break;
          }
          case websocketActions.create.type: {
            debugLog(
              'WEBSOCKET-MANAGER',
              'Create action received, connecting...',
            );

            authManager
              .getAuthData()
              .then(() => init(action as WebsocketCreateAction));
            break;
          }
          case websocketActions.close.type: {
            debugLog(
              'WEBSOCKET-MANAGER',
              'Close action received, disconnecting...',
            );
            ampli.websocketConnectionClosed({
              source: 'close_action',
            });

            disconnect();
            break;
          }
          case errorReconnectAttempt.toString(): {
            // NOTE: `die` is scoped to a single action handler invocation, so this
            // branch is effectively guarded by asyncCreateStatus (not die).
            if (
              die === false &&
              store.getState().websocket.meta.asyncCreateStatus !== 'pending'
            ) {
              next(websocketActions.reconnectAttempt());
            } else {
              debugLog(
                'WEBSOCKET-MANAGER',
                'Reconnect aborted, blocked by die/pending state',
              );
            }
            break;
          }
        }
      } catch (exception) {
        Sentry.captureException(
          new Error('Websocket: middleware exception caught'),
          { extra: { exception } },
        );
      }

      return next(action);
    };
  };
